import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from 'services/config';
import type {
  ICredentials,
  ILoginResponse,
  IUser,
  IMFAVerifyRequest,
  IMFADeviceVerifyRequest,
  IRegisterTrustedDeviceRequest,
  IRemoveTrustedDeviceRequest,
  TrustedDevicesList,
  IMFASetupResponse,
  IMFAConfirmRequest,
  IMFABackupCodesResponse,
} from '@/models/auth.ts';
import { prepareHeaders } from '@/state/slices/index.ts';

/** Avoid `Boolean("false") === true` and similar API quirks. */
function parseOptionalBool(value: unknown): boolean | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'no' || s === '') return false;
  }
  return undefined;
}

/** Send camelCase (DRF JSON) and snake_case so either serializer shape works. */
function dualMfaDevicePayload(mfaToken: string, deviceId: string) {
  return {
    mfaToken,
    deviceId,
    mfa_token: mfaToken,
    device_id: deviceId,
  };
}

function extractDeviceIdFromEntry(entry: unknown): string {
  if (typeof entry === 'string') return entry;
  if (entry && typeof entry === 'object') {
    const o = entry as Record<string, unknown>;
    const id = o.device_id ?? o.deviceId;
    if (typeof id === 'string') return id;
  }
  return '';
}

/** Normalize GET /auth/mfa/trusted-devices/ — supports several backend shapes. */
export function normalizeTrustedDevicesResponse(
  raw: unknown
): TrustedDevicesList {
  if (raw == null) return { deviceIds: [] };
  if (Array.isArray(raw)) {
    return {
      deviceIds: raw.map(extractDeviceIdFromEntry).filter(Boolean),
    };
  }
  const r = raw as Record<string, unknown>;
  if (Array.isArray(r.device_ids)) {
    return { deviceIds: (r.device_ids as string[]).filter(Boolean) };
  }
  if (Array.isArray(r.deviceIds)) {
    return { deviceIds: (r.deviceIds as string[]).filter(Boolean) };
  }
  const nested = r.results ?? r.devices ?? r.trusted_devices ?? r;
  if (Array.isArray(nested)) {
    return {
      deviceIds: nested.map(extractDeviceIdFromEntry).filter(Boolean),
    };
  }
  return { deviceIds: [] };
}

function normalizeLoginResponse(raw: unknown): ILoginResponse {
  const r = raw as Record<string, unknown>;
  const rememberDeviceEnabled = parseOptionalBool(
    r.rememberDeviceEnabled ?? r.remember_device_enabled
  );
  return {
    mfaRequired: Boolean(r.mfaRequired ?? r.mfa_required),
    mfaToken: (r.mfaToken ?? r.mfa_token) as string | undefined,
    rememberDeviceEnabled,
    id: r.id as string | undefined,
    email: r.email as string | undefined,
    firstName: (r.firstName ?? r.first_name) as string | undefined,
    lastName: (r.lastName ?? r.last_name) as string | undefined,
    role: r.role as string | undefined,
    token: r.token as string | undefined,
    mfaEnabled: (r.mfaEnabled ?? r.mfa_enabled) as boolean | undefined,
  };
}

export const authAPI = createApi({
  reducerPath: 'authAPI',
  baseQuery: fetchBaseQuery({
    baseUrl: config.BASE_URL,
    prepareHeaders,
  }),
  tagTypes: ['User', 'MFA'],
  endpoints: (builder) => ({
    login: builder.mutation<ILoginResponse, ICredentials>({
      query: (args: ICredentials) => ({
        url: '/auth/login/',
        method: 'POST',
        body: {
          email: args.username,
          password: args.password,
          ...(args.deviceId
            ? { device_id: args.deviceId, deviceId: args.deviceId }
            : {}),
        },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: normalizeLoginResponse,
      invalidatesTags: [{ type: 'User', id: 'current' }],
    }),
    mfaVerify: builder.mutation<IUser, IMFAVerifyRequest>({
      query: (args) => {
        const body: Record<string, string | boolean> = {
          mfaToken: args.mfaToken,
          mfa_token: args.mfaToken,
          code: args.code,
        };
        if (args.rememberDevice && args.deviceId) {
          body.remember_device = true;
          body.rememberDevice = true;
          body.device_id = args.deviceId;
          body.deviceId = args.deviceId;
        }
        return {
          url: '/auth/mfa/verify/',
          method: 'POST',
          body,
          headers: {
            'Content-Type': 'application/json',
          },
        };
      },
      invalidatesTags: [{ type: 'User', id: 'current' }],
    }),
    verifyTrustedDevice: builder.mutation<IUser, IMFADeviceVerifyRequest>({
      query: (args) => ({
        url: '/auth/mfa/verify-device/',
        method: 'POST',
        body: dualMfaDevicePayload(args.mfaToken, args.deviceId),
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: [{ type: 'User', id: 'current' }],
    }),
    mfaSetup: builder.mutation<IMFASetupResponse, void>({
      query: () => ({
        url: '/auth/mfa/setup/',
        method: 'POST',
      }),
    }),
    mfaConfirm: builder.mutation<IMFABackupCodesResponse, IMFAConfirmRequest>({
      query: (args) => ({
        url: '/auth/mfa/confirm/',
        method: 'POST',
        body: { code: args.code },
      }),
      invalidatesTags: ['MFA'],
    }),
    mfaBackupCodes: builder.query<IMFABackupCodesResponse, void>({
      query: () => ({
        url: '/auth/mfa/backup-codes/',
      }),
      providesTags: ['MFA'],
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/auth/logout/',
        method: 'POST',
      }),
    }),
    clearTrustedDevices: builder.mutation<{ detail: string }, void>({
      query: () => ({
        url: '/auth/mfa/trusted-devices/',
        method: 'DELETE',
      }),
      invalidatesTags: ['MFA'],
    }),
    getTrustedDevices: builder.query<TrustedDevicesList, void>({
      query: () => ({
        url: '/auth/mfa/trusted-devices/',
        method: 'GET',
      }),
      transformResponse: normalizeTrustedDevicesResponse,
      providesTags: ['MFA'],
    }),
    removeTrustedDevice: builder.mutation<
      { detail: string },
      IRemoveTrustedDeviceRequest
    >({
      query: ({ deviceId }) => ({
        url: '/auth/mfa/trusted-devices/',
        method: 'DELETE',
        params: { device_id: deviceId, deviceId },
      }),
      invalidatesTags: ['MFA'],
    }),
    registerTrustedDevice: builder.mutation<
      { detail: string },
      IRegisterTrustedDeviceRequest
    >({
      query: (args) => ({
        url: '/auth/mfa/trusted-devices/',
        method: 'POST',
        body: { device_id: args.deviceId, deviceId: args.deviceId },
      }),
      invalidatesTags: ['MFA'],
    }),
    getCurrentUser: builder.query<IUser, string>({
      query: (token: string) => ({
        url: 'users/me',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      providesTags: [{ type: 'User', id: 'current' }],
    }),
  }),
});

export const {
  useLoginMutation,
  useMfaVerifyMutation,
  useVerifyTrustedDeviceMutation,
  useMfaSetupMutation,
  useMfaConfirmMutation,
  useLazyMfaBackupCodesQuery,
  useLogoutMutation,
  useClearTrustedDevicesMutation,
  useGetTrustedDevicesQuery,
  useRemoveTrustedDeviceMutation,
  useRegisterTrustedDeviceMutation,
} = authAPI;
